import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  iconClasses,
  useTheme,
} from "@mui/material";

import {
  ChevronLeft,
  ChevronRightOutlined,
  HomeOutlined,
  Groups2Outlined,
  ReceiptLongOutlined,
  ShoppingCart,
  NotificationsOutlined,
  Event,
  Category,
  AdminPanelSettingsOutlined,
  SecurityOutlined,
  VpnKeyOutlined,
  GroupAdd,
  WorkOutline,
  FeedbackOutlined,
  FitnessCenterOutlined,
  TimelineOutlined,
  AccountBalanceWalletOutlined,
  AccountBalanceOutlined,
  PaidOutlined,
  PhotoLibraryOutlined,
  StarRateOutlined,
  ChatOutlined,
} from "@mui/icons-material";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import bhetamana from "../assets/vetumnata_logo.jpeg";

const navItems = [
  {
    text: "DASHBOARD",
    icon: <HomeOutlined />,
  },
  {
    text: "MANAGEMENT",
    icon: null,
  },
  {
    text: "ADMIN",
    icon: <AdminPanelSettingsOutlined />,
  },
  {
    text: "ROLE",
    icon: <SecurityOutlined />,
  },
  {
    text: "PERMISSION",
    icon: <VpnKeyOutlined />,
  },
  {
    text: "USER",
    icon: <Groups2Outlined />,
  },
  {
    text: "CONTENT",
    icon: null,
  },
  {
    text: "PACKAGE",
    icon: <ShoppingCart />,
  },
  {
    text: "EVENT",
    icon: <Event />,
  },
  {
    text: "CATEGORY",
    icon: <Category />,
  },
  {
    text: "POSTS",
    icon: <PhotoLibraryOutlined />,
  },
  {
    text: "NOTIFICATION",
    icon: <NotificationsOutlined />,
  },
  {
    text: "PROFESSION",
    icon: <WorkOutline />,
  },
  {
    text: "FEEDBACK",
    icon: <FeedbackOutlined />,
  },
  {
    text: "SUPPORT CHAT",
    icon: <ChatOutlined />,
  },
  {
    text: "RATING",
    icon: <StarRateOutlined />,
  },
  {
    text: "WEIGHT CALC",
    icon: <FitnessCenterOutlined />,
  },
  {
    text: "REPORTS",
    icon: null,
  },
  {
    text: "MEETUP",
    icon: <GroupAdd />,
  },
  {
    text: "TRANSACTIONS",
    icon: <ReceiptLongOutlined />,
  },
  {
    text: "USER ACTIVITY",
    icon: <TimelineOutlined />,
  },
  {
    text: "WALLET",
    icon: <AccountBalanceWalletOutlined />,
  },
  {
    text: "WITHDRAWALS",
    icon: <PaidOutlined />,
  },
  {
    text: "COMPANY WALLET",
    icon: <AccountBalanceOutlined />,
  },
  // {
  //   text: "LOCATION",
  //   icon: <ReceiptLongOutlined />,
  // },
];

const Sidebar = ({
  user,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              boxSixing: "border-box",
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box
              m="1.5rem 1.5rem 1.25rem 1.5rem"
              pb="1.25rem"
              sx={{
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                position: "relative",
              }}
            >
              <Box display="flex" justifyContent="center" alignItems="center">
                <Box
                  component="img"
                  alt="Vetumnata"
                  src={bhetamana}
                  height="80px"
                  width="80px"
                  sx={{ objectFit: "cover", borderRadius: "0.7rem" }}
                />
              </Box>
              {!isNonMobile && (
                <IconButton
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <ChevronLeft />
                </IconButton>
              )}
            </Box>

            <List sx={{ px: "0.75rem" }}>
              {navItems.map(({ text, icon }) => {
                if (!icon) {
                  return (
                    <Typography
                      key={text}
                      sx={{
                        m: "1.5rem 0 0.5rem 0.75rem",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.09em",
                        color: theme.palette.secondary[200],
                        opacity: 0.6,
                      }}
                    >
                      {text}
                    </Typography>
                  );
                }
                const lcText = text.toLowerCase().replace(/\s+/g, "");
                const isActive = active === lcText;

                return (
                  <ListItem key={text} disablePadding sx={{ mb: "2px" }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/${lcText}`);
                        setActive(lcText);
                      }}
                      sx={{
                        borderRadius: "0.6rem",
                        position: "relative",
                        backgroundColor: isActive
                          ? "rgba(163, 112, 209, 0.16)"
                          : "transparent",
                        color: isActive
                          ? theme.palette.secondary[100]
                          : theme.palette.secondary[200],
                        transition: "background-color 150ms ease, color 150ms ease",
                        "&:hover": {
                          backgroundColor: isActive
                            ? "rgba(163, 112, 209, 0.22)"
                            : "rgba(255,255,255,0.05)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "20%",
                          height: "60%",
                          width: "3px",
                          borderRadius: "0 4px 4px 0",
                          backgroundColor: "#a370d1",
                          opacity: isActive ? 1 : 0,
                          transition: "opacity 150ms ease",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "0.75rem",
                          minWidth: "40px",
                          color: isActive ? "#a370d1" : theme.palette.secondary[200],
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          fontWeight: isActive ? 700 : 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
